// features/workstations/qqq/today/economic-calendar-card.js
// ═══════════════════════════════════════════════════════════
// Economic Calendar Card
// ═══════════════════════════════════════════════════════════
//
// Per docs/QQQ_DASHBOARD_REBUILD_SPEC.md Section IV-A.
// Consumes /api/qqq/calendar/today (Phase 2a-1).
//
// Renders today's events with importance markers + countdown timer
// to the next high-importance event. Highlights the "event_day" flag.
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';

const IMPORTANCE_DISPLAY = {
  high:   { label: 'HIGH',   tone: 'terra' },
  medium: { label: 'MED',    tone: 'gold' },
  low:    { label: 'LOW',    tone: 'sage' },
};

export default function EconomicCalendarCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/qqq/calendar/today', { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <CardShell><div className="qqq-calendar-card__loading">Loading calendar…</div></CardShell>;
  if (error) return <CardShell><div className="qqq-calendar-card__error">Error: {error}</div></CardShell>;

  const events = data?.events || [];
  const eventDayBadge = data?.event_day
    ? <span className="qqq-calendar-card__event-badge qqq-calendar-card__event-badge--event">EVENT DAY</span>
    : <span className="qqq-calendar-card__event-badge qqq-calendar-card__event-badge--normal">NORMAL</span>;

  return (
    <CardShell>
      <div className="qqq-calendar-card__header">
        <div className="qqq-calendar-card__title">Economic Calendar — {data?.date || 'today'}</div>
        {eventDayBadge}
        <button type="button" onClick={load} className="qqq-calendar-card__refresh-btn">Refresh</button>
      </div>

      {data?.manual_override && (
        <div className="qqq-calendar-card__override">
          Manual override active: <strong>{data.manual_override}</strong>
        </div>
      )}

      {events.length === 0 ? (
        <div className="qqq-calendar-card__empty">No events scheduled for today.</div>
      ) : (
        <div className="qqq-calendar-card__events">
          {events.map((ev, i) => {
            const imp = IMPORTANCE_DISPLAY[ev.importance] || IMPORTANCE_DISPLAY.low;
            return (
              <div
                key={i}
                className={`qqq-calendar-card__event qqq-calendar-card__event--${imp.tone}`}
              >
                <div className="qqq-calendar-card__event-time">
                  {formatTime(ev.time)}
                </div>
                <div className={`qqq-calendar-card__event-importance qqq-calendar-card__event-importance--${imp.tone}`}>
                  {imp.label}
                </div>
                <div className="qqq-calendar-card__event-name">
                  {ev.name}
                  {ev.class && <span className="qqq-calendar-card__event-class"> · {ev.class}</span>}
                </div>
                <div className="qqq-calendar-card__event-values">
                  {ev.expected != null && <span>est: {ev.expected}</span>}
                  {ev.prior != null && <span>prior: {ev.prior}</span>}
                  {ev.actual != null && <span className="qqq-calendar-card__event-actual">actual: {ev.actual}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="qqq-calendar-card__footer">
        Last sync: {data?.fetched_at ? new Date(data.fetched_at).toLocaleString() : 'never'}
      </div>
    </CardShell>
  );
}

function formatTime(t) {
  if (!t) return '—:—';
  // t is HH:MM:SS; format to HH:MM ET
  const m = String(t).match(/(\d{2}):(\d{2})/);
  if (!m) return t;
  return `${m[1]}:${m[2]}`;
}

function CardShell({ children }) {
  return <div className="qqq-calendar-card">{children}</div>;
}
